import classNames from 'classnames'
import { FC, SVGProps } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg'
import { ReactComponent as DeleteIcon } from 'src/assets/svg/report-delete.svg'
import { ReactComponent as DownloadIcon } from 'src/assets/svg/report-download.svg'
import { ReactComponent as ReportPdfIcon } from 'src/assets/svg/report-pdf.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { MODULE_META, ModuleMeta } from './moduleMeta'
import styles from './reportCard.module.scss'
import { Report } from '../interfaces/report.interface'

// Campaign Comparison reuses this card for its runs. The two differ only in the
// thumbnail, the pill and a few strings, so they are props rather than a second
// copy of the card. Every one is optional: unset, the card reads as a PDF
// report exactly as it did before.
export interface ReportCardCopy {
  failedSubtext?: string
  // Takes the row because both callers build it from the report itself.
  failedTitle?: (report: Report) => string
  generating?: string
  retry?: string
}

interface ReportCardProps {
  // Drops the card to the 104px comparison row height.
  compact?: boolean
  copy?: ReportCardCopy
  icon?: FC<SVGProps<SVGSVGElement>>
  iconClassName?: string
  onDelete: () => void
  onDownload: () => void
  // Opens the row's own page. Left unset the card is not clickable, which is
  // how Your Reports uses it — a PDF is downloaded, not opened in the app.
  onOpen?: () => void
  onRetry: () => void
  // Overrides the module pills — a comparison has one fixed pill of its own
  // rather than a module list.
  pills?: ModuleMeta[]
  report: Report
}

const DEFAULT_COPY: Required<ReportCardCopy> = {
  failedSubtext: 'We couldn’t generate this report due to a temporary issue.',
  failedTitle: (report) => `${report.name} - Report Generation failed`,
  generating: 'Generating report…',
  retry: 'Retry Generate Report',
}

export const ReportCard: FC<ReportCardProps> = ({
  compact = false,
  copy,
  icon: Icon = ReportPdfIcon,
  iconClassName,
  onDelete,
  onDownload,
  onOpen,
  onRetry,
  pills,
  report,
}) => {
  const text = { ...DEFAULT_COPY, ...copy }
  const cardPills = pills ?? report.modules.map((module) => MODULE_META[module])

  // Shared by the ready and downloading states — both show when the report was
  // generated and how big it is.
  const renderMeta = () => (
    <div className={styles.meta}>
      <span className={styles.metaDate}>Generated {report.generatedAt}</span>
      <span className={styles.metaSize}>{report.fileSize}</span>
    </div>
  )

  const renderDeleteButton = () => (
    <button
      aria-label="Delete report"
      className={styles.iconButton}
      onClick={(event) => {
        event.stopPropagation()
        onDelete()
      }}
      type="button"
    >
      <DeleteIcon className={styles.buttonIcon} />
    </button>
  )

  const renderRight = () => {
    if (report.status === 'generating') {
      return (
        <div className={styles.generatingRow}>
          <span className={styles.generatingLabel}>{text.generating}</span>

          {/* Indeterminate unless the source reports a percentage: the PDF
              render happens in a single pass and has no notion of being partway
              done, so a number there would be invented. The bar animates to
              show work is happening without claiming to know how much is left. */}
          <div className={styles.progressTrack}>
            {report.progress === undefined ? (
              <div className={styles.progressIndeterminate} />
            ) : (
              <div
                className={styles.progressFill}
                style={{ width: `${report.progress}%` }}
              />
            )}
          </div>

          {report.progress !== undefined && (
            <span className={styles.progressValue}>{report.progress}%</span>
          )}
        </div>
      )
    }

    if (report.status === 'downloading') {
      return (
        <>
          {renderMeta()}

          <span className={styles.downloadingPill}>
            <FontAwesomeIcon
              className={styles.downloadingSpinner}
              icon={faSpinner}
              spin
            />
            Downloading…
          </span>

          {renderDeleteButton()}
        </>
      )
    }

    return (
      <>
        {renderMeta()}

        <button
          aria-label="Download report"
          className={styles.iconButton}
          onClick={(event) => {
            event.stopPropagation()
            onDownload()
          }}
          type="button"
        >
          <DownloadIcon className={styles.buttonIcon} />
        </button>

        {renderDeleteButton()}
      </>
    )
  }

  // A failed report has no PDF, size or generated date to show, so the whole
  // card is replaced rather than leaving empty slots where those would be.
  if (report.status === 'failed') {
    return (
      <article className={classNames(styles.reportCard, styles.failedCard)}>
        <div className={styles.failedLeft}>
          <WarningIcon className={styles.failedIcon} />

          <div className={styles.failedText}>
            <h4 className={styles.failedTitle}>{text.failedTitle(report)}</h4>
            <p className={styles.failedSubtext}>{text.failedSubtext}</p>
          </div>
        </div>

        <div className={styles.failedActions}>
          <button
            className={styles.failedDeleteButton}
            onClick={onDelete}
            type="button"
          >
            Delete
          </button>

          <button
            className={styles.failedRetryButton}
            onClick={onRetry}
            type="button"
          >
            {text.retry}
          </button>
        </div>
      </article>
    )
  }

  return (
    <article
      className={classNames(styles.reportCard, {
        [styles.compactCard]: compact,
        [styles.clickableCard]: !!onOpen,
      })}
      // The row's own buttons stop propagation via their handlers below, so a
      // download or delete never opens the report behind them.
      onClick={onOpen}
    >
      <div className={styles.cardLeft}>
        <Icon className={iconClassName ?? styles.pdfIcon} />

        <div className={styles.reportText}>
          <h4 className={styles.reportName}>{report.name}</h4>
          {/* A comparison names its two ranges in the title itself, so there is
              no second date line to render beneath it. */}
          {!!report.dateRange && (
            <span className={styles.reportDate}>{report.dateRange}</span>
          )}

          <div className={styles.pills}>
            {cardPills.map(({ background, color, icon: PillIcon, label }) => {
              return (
                <span
                  className={styles.pill}
                  key={label}
                  style={{ background, color }}
                >
                  <PillIcon className={styles.pillIcon} />
                  {/* Pills are forced onto one row, so a crowded card
                      ellipsises its labels. TruncatedTooltip measures the span
                      and only shows a tooltip when the text is genuinely cut —
                      no tooltip on pills that fit. */}
                  <TruncatedTooltip className={styles.pillLabel} text={label} />
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <div className={styles.cardRight}>{renderRight()}</div>
    </article>
  )
}
